import { isMockId } from '@gp/utils/common';

import type { IRecord, IMockIdable, IProjectBaseDTO } from '@gp/types/global';
import type { IDPSReportContextVO } from '@gp/types/dpsReport/common';
import type { ICommonProjectMilestoneDTOV2 } from '@gp/types/dpsReport/v2';

/**
 * 分离当前已添加的集合中 初始化拉取的集合（GR的数据） 和 手动添加的集合
 * @param list 当前已添加的集合
 * @returns currentInitial 当前已添加的集合中 初始化拉取的集合（GR的数据）
 * @returns currentAdd 当前已添加的集合中 手动添加的集合
 */
const separateCurrentCollection = <T extends IMockIdable>(
  // 当前已添加的集合
  list: T[],
): {
  // 当前已添加的集合中 初始化拉取的集合（GR的数据）
  currentInitial: T[];
  // 当前已添加的集合中 手动添加的集合
  currentAdd: T[];
} => {
  // 当前已添加的集合中 初始化拉取的集合（GR的数据）
  const currentInitial: T[] = [];
  // 当前已添加的集合中 手动添加的集合
  const currentAdd: T[] = [];

  for (const item of list) {
    const { mock_id: mockId } = item || {};
    // 过滤没有mock_id的数据，判定为垃圾数据
    if (!mockId) continue;
    // mock_id值为mockId类型时，判定为 手动添加的数据，放入到手动添加的集合中
    if (isMockId(mockId)) {
      currentAdd.push(item);
    } else {
      // 否则判定为初始化拉取的数据，放入到初始化拉取的集合中
      currentInitial.push(item);
    }
  }

  return { currentInitial, currentAdd };
};

const getReversedData = (payload?: any, ignoreKeys?: string[]): IRecord<any> => {
  if (!Array.isArray(ignoreKeys) || !ignoreKeys.length || !payload) return {};
  return ignoreKeys.reduce((acc, cur) => {
    const curVal = payload?.[cur];
    if (!curVal) return acc;
    return { ...acc, [cur]: curVal };
  }, {});
};

/**
 * 获取新的手动添加的集合
 * 1、删除掉本次刷新数据后可手动添加的集合中不包含的数据
 * 2、相同的数据，进行字段更新
 * @param list 当前手动添加的集合
 * @param projectsMilestones 本次刷新数据后可手动添加的集合
 * @param getRepeatCheckKey 生成重复检验key的方法（判定数据是否相同的标准）
 * @returns 新的手动添加的集合
 */
const getNewAddCollection = <T1 extends IProjectBaseDTO, T2 extends ICommonProjectMilestoneDTOV2>(
  list: T1[],
  projectsMilestones: T2[],
  ignoreKeys: string[],
  getRepeatCheckKey: (payload: any) => string,
): T1[] => {
  if (!Array.isArray(list) || !list.length) return [];
  if (!Array.isArray(projectsMilestones) || !projectsMilestones.length) return list;
  // 刷新数据后的 手动添加的集合
  const newAdd: T1[] = [];
  for (const item of list) {
    // 找到projectsMilestones中匹配item的数据（根据project_id是否一致匹配）
    const itemProject = projectsMilestones.find((subItem) => subItem?.project_id === item?.project_id);
    // 过滤没匹配上的
    if (!itemProject) continue;
    // 生成item的重复校验key（判定是否数据相同的标准）
    const itemRepeatCheckKey = getRepeatCheckKey(item);
    // 找到itemProject?.details中匹配itemRepeatCheckKey的数据（即匹配的milestone数据）
    const itemMilestone = itemProject?.details?.find((subItem) => {
      // 根据itemProject、subItem 生成重复检验的key
      const subItemRepeatCheckKey = getRepeatCheckKey({ ...itemProject, ...subItem });
      return itemRepeatCheckKey === subItemRepeatCheckKey;
    });
    // 如果匹配成功，刷新这条数据的 可刷新的字段值
    if (itemMilestone) {
      const itemProjectTmp = { ...itemProject };
      // @ts-ignore
      delete itemProjectTmp.details;
      // 根据指定忽略的字段 构建 需要保留的对象，在下面对象合并时，避免指定忽略的字段被覆盖
      const reversedItem = getReversedData(item, ignoreKeys);
      newAdd.push({ ...item, ...itemProjectTmp, ...itemMilestone, ...reversedItem });
    }
  }
  return newAdd;
};

/**
 * 获取新的初始化拉取的集合
 * 1、commingInitial为空时，直接返回空数组
 * 2、分别生成currentInitialRepeatCheckKeys、newAddRepeatCheckKeys两个key值集合
 * 3、遍历commingInitial集合
 *    1、调用getRepeatCheckKey方法生成当前元素对应的repeatCheckKey
 *    2、如果newAddRepeatCheckKeys集合中不包含当前元素对应的repeatCheckKey，则根据currentInitialRepeatCheckKeys集合以及repeatCheckKey匹配到当前元素 对应的 currentInitial集合中的索引，
 *      再做数据合并处理，并将数据合并处理后的数据添加到newAdd集合中
 * @param currentInitial 当前初始化拉取的集合
 * @param commingInitial 本次数据刷新 初始化拉取的集合
 * @param newAdd 本次数据刷新后 新的手动添加的集合
 * @param ignoreKeys 指定忽略的字段，合并每条数据时，需要忽略的字段
 * @param getRepeatCheckKey 生成重复检验key的方法（判定数据是否相同的标准）
 * @returns 新的初始化拉取的集合
 */
const getNewInitialCollection = <T extends IDPSReportContextVO>(
  currentInitial: T[],
  commingInitial: T[],
  newAdd: T[],
  ignoreKeys: string[],
  getRepeatCheckKey: (payload: any) => string,
): T[] => {
  if (!Array.isArray(commingInitial) || !commingInitial.length) return [];
  const currentInitialRepeatCheckKeys = currentInitial?.map(getRepeatCheckKey) || [];
  const newAddRepeatCheckKeys = newAdd?.map(getRepeatCheckKey) || [];
  const newInitial = [];
  for (const item of commingInitial) {
    const repeatCheckKey = getRepeatCheckKey(item);
    if (!newAddRepeatCheckKeys?.includes(repeatCheckKey)) {
      const index = currentInitialRepeatCheckKeys?.indexOf(repeatCheckKey);
      const indexItem = currentInitial[index];
      // 根据指定忽略的字段 构建 需要保留的对象，在下面对象合并时，避免指定忽略的字段被覆盖
      const reversedItem = getReversedData(indexItem, ignoreKeys);
      newInitial.push({
        ...indexItem,
        ...item,
        ...reversedItem,
      });
    }
  }
  return newInitial;
};

/**
 * 数据刷新处理
 * 1、projectsMilestones为空数组时，代表没有可添加的数据，直接返回空数组
 * 2、current为空数组时，initial有数据就返回initial，否则返回空数组
 * 3、调用separateCurrentCollection方法，把current集合拆分为两个集合，currentInitial（初始化拉取的集合）、currentAdd（手动添加的集合）两个集合
 * 4、调用getNewAddCollection方法，获取处理后的新的手动添加的集合
 * 5、调用getNewInitialCollection方法，获取处理后的新的初始化拉取的集合
 * 6、组合新的初始化拉取的集合、新的手动添加的集合
 * @param params 参数对象
 * @param params.current 当前集合（包含初始化拉取的集合、手动添加的集合）
 * @param params.initial 本次数据刷新 拉取的初始化的集合
 * @param params.projectsMilestones 本次数据刷新 拉取所有集合（包含初始化的集合、可手动添加的集合）
 * @param params.ignoreKeys 指定忽略的字段，合并每条数据时，需要忽略的字段
 * @param params.getRepeatCheckKey 生成重复检验key的方法（判定数据是否相同的标准）
 * @returns 数据刷新处理后返回的集合
 */
export const refreshCollection = <T1 extends IDPSReportContextVO, T2 extends ICommonProjectMilestoneDTOV2>(params: {
  current: T1[];
  initial: T1[];
  projectsMilestones: T2[];
  ignoreKeys: string[];
  getRepeatCheckKey: (payload: any) => string;
}): T1[] => {
  const { current, initial, projectsMilestones, ignoreKeys, getRepeatCheckKey } = params || {};
  // 没有可添加的project，直接返回空数组
  if (!Array.isArray(projectsMilestones) || !projectsMilestones.length) return [];
  // 当前没有添加project，直接返回初始化project数据
  if (!Array.isArray(current) || !current.length) return Array.isArray(initial) && initial.length > 0 ? initial : [];
  // 把当前添加的project拆分为初始化的project数据、手动添加的project数据 两组
  const { currentInitial, currentAdd } = separateCurrentCollection(current);
  // 获取新的手动添加的集合
  const newAdd = getNewAddCollection(currentAdd, projectsMilestones, ignoreKeys, getRepeatCheckKey);
  // 获取新的初始化拉取的集合
  const newInitial = getNewInitialCollection(currentInitial, initial, newAdd, ignoreKeys, getRepeatCheckKey);
  return [...newInitial, ...newAdd];
};
